import { useState, useEffect } from 'react';
import { Dialog, DialogContent, Box, alpha, useTheme, Button } from '@mui/material';
import Logout from '@mui/icons-material/Logout';
import { useSnackbar } from 'notistack';
import useAuth from '@modules/auth/hooks/api/useAuth';
import useOpeningTimes from '@modules/account/hooks/api/useOpeningTimes';
import VideoStep from '@common/components/lib/partials/onboarding/components/VideoStep';
import OpeningTimeStep from '@common/components/lib/partials/onboarding/components/OpeningTimeStep';
import CalendarStep from '@common/components/lib/partials/onboarding/components/CalendarStep';
import { WeeklyOpeningTimeInput } from '@common/components/lib/partials/onboarding/defs/types';
import { OnboardingStepEnum } from '@modules/users/defs/types';
import { useTracking } from '@common/hooks/useTracking';
import { useRouter } from 'next/router';

interface OnboardingDialogProps {
  open: boolean;
  onComplete?: () => void;
}

const OnboardingDialog = ({ open = false, onComplete }: OnboardingDialogProps) => {
  const theme = useTheme();
  const { updateOnboardingStep, skipOnboardingStep, user, logout } = useAuth();
  const {
    saveWeeklyOpeningTime,
    data: openingTimeData,
    disconnectCalendar,
    connectCalendar,
    mutate: mutateOpeningTime,
  } = useOpeningTimes();
  const { enqueueSnackbar } = useSnackbar();
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const { trackEvent } = useTracking();
  const router = useRouter();
  const [_reconnectErrorMessage, setReconnectErrorMessage] = useState<string | null>(null);

  const connectedCalendars = openingTimeData?.connectedCalendars || [];

  useEffect(() => {
    if (currentStep === 1) {
      trackEvent('onboarding.welcome_video_page_view', {
        userId: user?.id,
      });
    } else if (currentStep === 2) {
      trackEvent('onboarding.opening_times_page_view', {
        userId: user?.id,
      });
    } else if (currentStep === 3) {
      trackEvent('onboarding.calendar_page_view', {
        userId: user?.id,
      });
    }
  }, [currentStep]);
  useEffect(() => {
    if (open) {
      checkOnboardingStatus();
    }
  }, [open]);

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    const connection = Array.isArray(router.query.connection)
      ? router.query.connection[0]
      : router.query.connection;
    const message = Array.isArray(router.query.message)
      ? router.query.message[0]
      : router.query.message;
    const errorCode = Array.isArray(router.query.error_code)
      ? router.query.error_code[0]
      : router.query.error_code;
    const errorMessage = Array.isArray(router.query.error_message)
      ? router.query.error_message[0]
      : router.query.error_message;

    const isPermissionScopeError =
      (connection === 'error' || connection === 'reconnect_calendar') &&
      (message === 'missing_calendar_scope' || errorCode === 'missing_calendar_scope_permissions');

    if (isPermissionScopeError) {
      setReconnectErrorMessage(
        errorMessage ||
          "Une erreur est survenue. Assurez-vous d'avoir coché les cases de permissions."
      );

      return;
    }

    setReconnectErrorMessage(null);
  }, [
    router.isReady,
    router.query.connection,
    router.query.message,
    router.query.error_code,
    router.query.error_message,
  ]);

  const checkOnboardingStatus = async () => {
    if (!user) {
      return;
    }

    setIsInitializing(true);
    try {
      const onboardingStep = user.onboardingStep;
      if (onboardingStep === OnboardingStepEnum.WELCOME_VIDEO) {
        setCurrentStep(1);
      } else if (onboardingStep === OnboardingStepEnum.CONFIGURE_WEEKLY_OPENING_TIMES) {
        setCurrentStep(2);
      } else if (onboardingStep === OnboardingStepEnum.CONNECT_CALENDARS) {
        setCurrentStep(3);
      } else if (onboardingStep === OnboardingStepEnum.RECONNECT_CALENDARS) {
        setCurrentStep(3);
      } else if (onboardingStep === OnboardingStepEnum.COMPLETED) {
        onComplete?.();
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      setCurrentStep(1);
    } finally {
      setIsInitializing(false);
    }
  };

  const handleCompleteVideoStep = async () => {
    setIsLoading(true);
    try {
      const response = await updateOnboardingStep({ step: OnboardingStepEnum.WELCOME_VIDEO });
      if (response.success) {
        setCurrentStep(2);
      } else {
        enqueueSnackbar('Une erreur est survenue', {
          variant: 'error',
        });
      }
    } catch (error) {
      console.error('Error completing step 1:', error);
      enqueueSnackbar('Une erreur est survenue', {
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveOpeningTime = async (openingTimes: WeeklyOpeningTimeInput[]) => {
    try {
      if (saveWeeklyOpeningTime) {
        await saveWeeklyOpeningTime(openingTimes);
      }

      const response = await updateOnboardingStep({
        step: OnboardingStepEnum.CONFIGURE_WEEKLY_OPENING_TIMES,
      });
      if (response.success) {
        return;
      }
      throw new Error('Failed to update onboarding step');
    } catch (error) {
      console.error('Error saving openingTime:', error);
      enqueueSnackbar('Une erreur est survenue', {
        variant: 'error',
      });
      throw error;
    }
  };

  const handleConnectCalendar = async (provider: 'google' | 'microsoft') => {
    try {
      await connectCalendar(provider);
      trackEvent('onboarding.completed', {
        userId: user?.id,
      });
    } catch (error) {
      console.error('Error connecting calendar:', error);
      enqueueSnackbar('Une erreur est survenue', {
        variant: 'error',
      });
      throw error;
    }
  };

  const handleDisconnectCalendar = async (provider: string) => {
    try {
      await disconnectCalendar(provider);
      enqueueSnackbar('Calendrier déconnecté', {
        variant: 'success',
      });
    } catch (error) {
      console.error('Error disconnecting calendar:', error);
      enqueueSnackbar('Une erreur est survenue', {
        variant: 'error',
      });
      throw error;
    }
  };

  const handleSkipCalendar = async () => {
    setIsLoading(true);
    try {
      const response = await skipOnboardingStep();
      if (response.success) {
        trackEvent('onboarding.completed', {
          userId: user?.id,
          skippedCalendar: true,
        });
        onComplete?.();
      } else {
        enqueueSnackbar('Une erreur est survenue', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error skipping calendar step:', error);
      enqueueSnackbar('Une erreur est survenue', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackStep = async () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      if (currentStep === 3) {
        await mutateOpeningTime();
      }
    }
  };

  const handleNextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  if (isInitializing || currentStep === 0) {
    return (
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            background: theme.palette.background.paper,
            boxShadow: theme.shadows[24],
            maxHeight: '90vh',
          },
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  mx: 'auto',
                  mb: 2,
                  borderRadius: '50%',
                  border: `3px solid ${theme.palette.primary.main}`,
                  borderTopColor: 'transparent',
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      // reduce overlay padding (MuiPaper-root)
      sx={{
        p: { xs: 0, sm: 3 },
        m: 0,
        '& .MuiPaper-root': {
          m: 1.5,
          width: '100%',
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      }}
      PaperProps={{
        sx: {
          background: 'transparent',
          boxShadow: 'none',
          maxHeight: '90vh',
        },
      }}
    >
      <Box
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          background: theme.palette.background.paper,
          boxShadow: theme.shadows[24],
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DialogContent
          sx={{
            p: 0,
          }}
        >
          <Box
            sx={{
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.06
              )} 0%, ${alpha(theme.palette.background.paper, 1)} 50%)`,
              p: { xs: 1, sm: 3 },
              minHeight: currentStep === 1 ? 'auto' : '550px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ width: '100%' }}>
              {currentStep === 1 && (
                <VideoStep onComplete={handleCompleteVideoStep} isLoading={isLoading} />
              )}
              {currentStep === 2 && (
                <OpeningTimeStep
                  onNext={handleNextStep}
                  onBack={handleBackStep}
                  isLoading={isLoading}
                  onSave={handleSaveOpeningTime}
                  existingOpeningTimes={openingTimeData?.weeklyOpeningTimes || []}
                />
              )}
              {currentStep === 3 && (
                <CalendarStep
                  onNext={handleNextStep}
                  onBack={
                    user?.onboardingStep === OnboardingStepEnum.RECONNECT_CALENDARS
                      ? undefined
                      : handleBackStep
                  }
                  onSkip={handleSkipCalendar}
                  isLoading={isLoading}
                  connectedCalendars={connectedCalendars}
                  onConnectCalendar={handleConnectCalendar}
                  onDisconnectCalendar={handleDisconnectCalendar}
                  isFirstTime={user?.onboardingStep !== OnboardingStepEnum.RECONNECT_CALENDARS}
                />
              )}
            </Box>
          </Box>
        </DialogContent>
      </Box>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="text"
          color="error"
          startIcon={<Logout />}
          onClick={() => logout()}
          sx={{
            px: 3,
            py: 1,
            backgroundColor: 'white',
            borderRadius: 1,
            boxShadow: 2,
            transition: theme.transitions.create(['background-color', 'box-shadow']),
          }}
        >
          Me déconnecter
        </Button>
      </Box>
    </Dialog>
  );
};

export default OnboardingDialog;
